/**
 * skill_breakdown 的两种形状,以及"轴太少别画雷达"。
 *
 * 单科报告是扁平的 {轴: {score,trend,delta}};综合报告按学科分组成
 * {学科: {name, axes: {...}}}。分组是必须的 —— 之前让 agent 把综合报告
 * 也摊平,它就把英语阅读 82 和数学读题 72 平均成了 77 挂在一根「阅读」
 * 轴上。那是个没有意义的数字,而且看不出来是拼的(Jarod 2026-08-31 追问
 * "不同学科的六边形图 key 是不是不一样"时发现)。
 *
 * 另一件:数学那次只有 2 根轴。两点画不出多边形,recharts 会画成一条线,
 * 看着像坏了。少于 3 根轴改用横条列表,信息一点不少,还更好读。
 */
import { RadarChart } from "../../components/RadarChart";

export interface AxisEntry {
  score: number | null;
  trend?: string;
  delta?: string;
}
type Flat = Record<string, AxisEntry>;
type Group = { name?: string; axes: Flat };

/** 分组形状的判据:每个值都带 axes 对象。存量报告是扁平的,照旧渲染。 */
function isGrouped(sb: unknown): sb is Record<string, Group> {
  if (!sb || typeof sb !== "object") return false;
  const vals = Object.values(sb as Record<string, unknown>);
  return (
    vals.length > 0 &&
    vals.every(
      (v) => !!v && typeof v === "object" && typeof (v as Group).axes === "object",
    )
  );
}

const MIN_RADAR_AXES = 3;

// `overall` 是总分不是能力维度 —— agent 契约里写了不要放进 axes,但这里
// 再挡一道:模型偶尔还是会塞进来,而雷达图上多一根「overall」辐条会和
// 真实技能混在一起,看不出哪个是哪个。
const NOT_AN_AXIS = new Set(["overall", "total", "average"]);

function axesOf(flat: Flat): { skill: string; score: number | null }[] {
  return Object.entries(flat)
    .filter(([skill]) => !NOT_AN_AXIS.has(skill.toLowerCase()))
    .map(([skill, info]) => ({
      skill,
      score: typeof info?.score === "number" ? info.score : null,
    }));
}

/** 轴少于 3 根时的替代呈现:带分数条的列表。 */
function AxisBars({
  axes,
  labelOf,
}: {
  axes: { skill: string; score: number | null }[];
  labelOf: (k: string) => string;
}) {
  return (
    <ul className="space-y-2">
      {axes.map((a) => (
        <li key={a.skill} className="flex items-center gap-3 text-sm">
          <span className="w-24 shrink-0 text-slate-600">{labelOf(a.skill)}</span>
          <span className="flex-1 h-2 rounded bg-slate-100 overflow-hidden">
            <span
              className="block h-full bg-sky-400"
              style={{ width: `${Math.max(0, Math.min(100, a.score ?? 0))}%` }}
            />
          </span>
          <span className="w-10 text-right tabular-nums text-slate-700">
            {a.score ?? "—"}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function SkillBreakdownView({
  breakdown,
  overall,
  lang,
  labelOf,
  height = 260,
}: {
  breakdown: unknown;
  overall: number | null;
  lang: "zh" | "en";
  labelOf: (k: string) => string;
  height?: number;
}) {
  if (!breakdown || typeof breakdown !== "object") return null;

  const render = (flat: Flat, ov: number | null, key?: string, title?: string) => {
    const axes = axesOf(flat);
    if (axes.length === 0) return null;
    const body =
      axes.length >= MIN_RADAR_AXES ? (
        <RadarChart data={{ axes, overall: ov }} height={height} lang={lang} />
      ) : (
        <AxisBars axes={axes} labelOf={labelOf} />
      );
    return (
      <div key={key ?? "single"}>
        {title && (
          <h4 className="text-xs font-medium text-slate-500 mb-2">{title}</h4>
        )}
        {body}
      </div>
    );
  };

  if (isGrouped(breakdown)) {
    const groups = Object.entries(breakdown);
    return (
      <div className={groups.length > 1 ? "grid grid-cols-1 lg:grid-cols-2 gap-4" : ""}>
        {groups.map(([slug, g]) =>
          render(g.axes, null, slug, g.name || slug),
        )}
      </div>
    );
  }
  return render(breakdown as Flat, overall);
}

/** 明细表格。分组形状时多一列「学科」,把各科的轴平铺成行 ——
 *  表格里同名轴不会被误解为同一根,因为学科列摆在旁边。 */
export function SkillBreakdownRows({
  breakdown,
  labelOf,
  subjectHeader,
}: {
  breakdown: unknown;
  labelOf: (k: string) => string;
  subjectHeader: string;
}): { grouped: boolean; rows: { subject?: string; skill: string; info: AxisEntry }[]; subjectHeader: string } {
  const rows: { subject?: string; skill: string; info: AxisEntry }[] = [];
  if (breakdown && typeof breakdown === "object") {
    if (isGrouped(breakdown)) {
      for (const [slug, g] of Object.entries(breakdown)) {
        for (const [skill, info] of Object.entries(g.axes ?? {})) {
          if (NOT_AN_AXIS.has(skill.toLowerCase())) continue;
          rows.push({ subject: g.name || slug, skill, info });
        }
      }
      return { grouped: true, rows, subjectHeader };
    }
    for (const [skill, info] of Object.entries(breakdown as Flat)) {
      if (NOT_AN_AXIS.has(skill.toLowerCase())) continue;
      rows.push({ skill, info });
    }
  }
  void labelOf;
  return { grouped: false, rows, subjectHeader };
}
